<script lang="ts" setup>
import {AppTypography} from "~/components/base";
import CheckSuccess from "~/components/icons/check-success.svg"
import CloseDanger from "~/components/icons/close-danger.svg"
import type {Bid} from '~/types/Bid';
import type {User} from '~/types/User';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const {
  bidDetails,
  jobPoster,
} = defineProps<{
  bidDetails: Bid
  jobPoster: User
}>()
</script>

<template>
  <v-card class="pa-6 bg-grey-lighten-4" flat>
    <AppTypography element="h5" type="title-5" class="mb-2">Your Offer Details</AppTypography>
    <p class="text-body-2 text-grey-darken-1 mb-6">
      Submitted on {{ dayjs(bidDetails.created_at).format("DD MMMM YYYY") }}
    </p>

    <v-card class="px-4 py-2 bg-white border-thin" flat>
      <div class="d-flex justify-space-between align-center py-5">
        <AppTypography element="p" type="subheadline-1">Your Offer</AppTypography>
        <AppTypography element="p" type="body-2">$ {{ bidDetails.rate }} / Per Hour</AppTypography>
      </div>
      <div class="d-flex justify-space-between align-center py-5">
        <AppTypography element="p" type="subheadline-1">Your Availability</AppTypography>
        <AppTypography element="p" type="body-2">{{
            bidDetails.availability === 'Choose Date' ?
                dayjs(bidDetails.availability_date).format("DD MMMM YYYY") :
                bidDetails.availability.charAt(0).toUpperCase() + bidDetails.availability.slice(1)
          }}
        </AppTypography>
      </div>
      <div class="d-flex justify-space-between align-center" v-if="bidDetails.is_licensed_insured">
        <AppTypography element="p" type="subheadline-1">Confirmation of license & Insurance</AppTypography>
        <v-checkbox color="black" :model-value="bidDetails.is_licensed_insured" hide-details readonly/>
      </div>
    </v-card>

    <v-card
        class="pa-4 mt-6 bg-white border-thin"
        v-if="bidDetails.notes"
        flat>
      <AppTypography element="p" type="subheadline-1" class="mb-2">Notes</AppTypography>
      <AppTypography element="p" type="body-1">{{ bidDetails.notes }}</AppTypography>
    </v-card>

    <v-card class="pa-4 mt-6 bg-white border-thin" flat v-if="bidDetails.status === 'accepted'">
      <AppTypography element="h5" type="title-5" class="mb-2">
        {{ jobPoster.first_name }} {{ jobPoster.last_name.charAt(0) }}.
      </AppTypography>
      <div class="mt-4 ga-1">
        <AppTypography element="p" type="footnote-1" style="color: #414156">Email</AppTypography>
        <AppTypography element="p" type="subheadline-1">{{ jobPoster.email }}</AppTypography>
      </div>
      <div class="mt-4 ga-1">
        <AppTypography element="p" type="footnote-1" style="color: #414156">Phone Number</AppTypography>
        <AppTypography element="p" type="subheadline-1">{{ jobPoster.phone_number }}</AppTypography>
      </div>
    </v-card>

    <v-card
        class="d-flex pa-4 mt-6 bg-white border-thin"
        v-if="bidDetails.status === 'unsuccessful'"
        flat
    >
      <div class="status-icon-container">
        <v-img :src="CloseDanger" height="24" width="24"/>
      </div>
      <div>
        <AppTypography element="p" type="footnote-2" class="mb-2 text-red-darken-1">OFFER UNSUCCESSFUL
        </AppTypography>
        <AppTypography element="p" type="subheadline-1">Thanks for submitting your offer. The listing poster has chosen
          another contractor for this job.
        </AppTypography>
      </div>
    </v-card>

    <v-card
        class="d-flex pa-4 mt-6 bg-white border-thin"
        v-if="bidDetails.status === 'accepted'"
        flat
    >
      <div class="status-icon-container">
        <v-img :src="CheckSuccess" height="24" width="24"/>
      </div>
      <div>
        <AppTypography element="p" type="footnote-2" class="mb-2 text-green-darken-1">OFFER ACCEPTED
        </AppTypography>
        <AppTypography element="p" type="subheadline-1">Congratulations! Your offer has been accepted by the job poster. Their
          contact details are now available, Please reach out to them to arrange the next steps.
        </AppTypography>
      </div>
    </v-card>

  </v-card>
</template>

<style scoped lang="scss">
.status-icon-container {
  padding: 18px 16px 0px 0px;
}
</style>